/**
 * The run orchestrator. Implements the Simulation contract (step at FIXED_DT) and owns every gameplay
 * system: director, chart scheduler, flight model, swept collision, Resonance and power-ups. It reads
 * simTick/beat, never wall time (FS-02 R10).
 *
 * One instance serves all six FS-09 modes (R02): a mode contributes only a `RunConfig` — a hull, a
 * seed and an ordered `RunSegment` plan — never a code path. `loadSegment` swaps the active sector
 * and its hazard-free lead-in; `onSegmentCleared` chains to the next segment, loops the plan
 * (Infinite/Zen) or ends the run. Everything else below is shared, unconditionally, by every mode.
 */
import { CULL_Z, IFRAME_DURATION, PLAY_RADIUS, SPAWN_Z } from '../core/constants';
import { LIVE_HAZARD_CAP } from '../core/constants';
import { createPool, type Pool } from '../core/pool';
import { createRng, type Rng } from '../core/rng';
import { GameState, type StateMachine } from '../core/state-machine';
import type { EventBus } from '../core/events';
import type { EventMap, HazardKind, PowerUpName } from '../core/event-map';
import type { Simulation } from '../core/loop';
import { AudioClock } from '../audio/clock';
import { TunnelWorld } from './tunnel-world';
import { Director } from './director';
import { ChartScheduler, configureParams, type Scheduler, type SpawnRequest } from './chart-scheduler';
import { OnboardingScheduler } from './onboarding-scheduler';
import type { OnboardingScript } from '../content/onboarding';
import { Hazard } from './hazards/instance';
import type { HazardParams } from './hazards/types';
import { ALL_KINDS, HAZARD_KINDS } from './hazards/kinds';
import { BossRunner } from './boss/runner';
import { BOSSES } from './boss/scripts';
import { customSectorDef, sectorByIndex, type SectorDef } from '../content/sectors';
import { PowerUps, GRANTABLE } from './powerups';
import { Pickups } from './pickups';
import type { RunConfig } from './run';
import { ENDLESS_INTENSITY_CAP, ENDLESS_INTENSITY_STEP, type RunSegment } from './modes/registry';
import { Input, type MutableInputFrame } from '../player/input';
import { createShipState, resetShip, stepFlight, type ShipState } from '../player/flight-model';
import { resolveModifiers, baseModifiers, type ModifierInput, type ResolvedModifiers } from '../player/modifiers';
import { testCollision, CollisionOutcome, type CollisionResult } from '../player/collision';
import { Resonance } from '../player/resonance';

const POWERUP_GRANT_INTERVAL = 14; // s
const BEAT_WINDOW_S = 0.06;
const AXIS_DEADZONE = 0.18;
const SECTOR_CLEAR_BONUS = 5000;

// Live caps from FS-08 §4.4's tier-restriction table — these kinds dominate the frame or become
// unreadable in quantity, so the limit is mechanical rather than advisory.
const MAX_LIVE_SLAB = 1;
const MAX_LIVE_FEEDBACK = 2;

export class Game implements Simulation {
  readonly world = new TunnelWorld();
  readonly audioClock = new AudioClock();
  sector: SectorDef = sectorByIndex(1);
  readonly director: Director;
  readonly resonance: Resonance;
  readonly powerups: PowerUps;
  readonly ship: ShipState = createShipState();
  readonly hazards: Pool<Hazard> = createPool('hazards', LIVE_HAZARD_CAP, () => new Hazard());
  readonly pickups: Pickups;

  private rng: Rng = createRng(1, 'run');
  /** The live chart, one per run. `scheduler` points at this or at the onboarding script. */
  private chartScheduler: ChartScheduler;
  private scheduler: Scheduler;
  private input: Input;
  /**
   * FS-01 §5.2's script, fetched at boot (R38 — data, not code). Null when the fetch or the schema
   * check failed: a first run without teaching is worse than one with it, but a run that never
   * starts because of a 404 is not a run at all, so onboarding is simply skipped.
   */
  private onboardingScript: OnboardingScript | null = null;
  private onboarding: OnboardingScheduler | null = null;

  private frame: MutableInputFrame = { ax: 0, ay: 0, usePowerup: false, pause: false, tick: 0 };
  private mods: ResolvedModifiers = baseModifiers();
  private modInput: ModifierInput = {
    intangible: false,
    worldSpeedScale: 1,
    hitboxScale: 1,
    accelScale: 1,
    beatAwardBonus: 1,
    assistHitbox: false,
    assistSpeed: false,
  };
  private collision: CollisionResult = {
    outcome: CollisionOutcome.None,
    nodeCentre: false,
    u: 0,
    distance: 0,
    nx: 0,
    ny: 0,
  };

  // ── Run configuration (FS-09 §3.2) ────────────────────────────────────────
  config: RunConfig | null = null;
  private plan: readonly RunSegment[] = [];
  segmentIndex = 0;
  /** Segment the run was in when it last ended — the `movement-start` retry anchor (FS-09 §4.5). */
  lastEndedSegmentIndex = 0;
  private loopCount = 0;
  private customSector: SectorDef | null = null;
  private grantPool: PowerUpName[] = GRANTABLE;

  private inTransition = false;
  private transitionSecondsLeft = 0;

  hazardsCleared = 0;
  highestSectorCleared = 0;
  hitsTakenCount = 0;
  staggerCount = 0;
  private grantTimer = POWERUP_GRANT_INTERVAL;
  private lastBeatSteerBeat = -1;
  private prevAxSign = 0;
  deathCause = '';
  won = false;
  private grantRng: Rng = createRng(1, 'grant');
  /** Latched for the run so the first-ever emphasis is spent once (FS-07 R36, F22). */
  private seenAPickup = false;

  // Boss.
  readonly boss = new BossRunner();
  private bossTriggered = false;
  private bossFighting = false;
  private bossRng: Rng = createRng(1, 'boss-pattern');
  private bossParams: HazardParams = {
    amp: 0,
    freq: 0,
    phase: 0,
    nodeTheta: 0,
    nodeHalfTheta: 0,
    nodeRMin: 0,
    nodeRMax: PLAY_RADIUS,
    polarity: 'rim',
    reach: 1,
    count: 0,
    rate: 0,
    seed: 0,
  };

  private onTrauma: (amount: number) => void = () => {};

  constructor(
    private bus: EventBus<EventMap>,
    private machine: StateMachine,
  ) {
    this.director = new Director(bus);
    this.resonance = new Resonance(bus);
    this.powerups = new PowerUps(bus, {
      fillResonance: () => this.resonance.fillToMax(),
      wavebreaker: () => this.wavebreaker(),
    });
    this.chartScheduler = new ChartScheduler(this.rng.fork('hazard-jitter'), this.audioClock);
    this.scheduler = this.chartScheduler;
    this.pickups = new Pickups(bus, this.rng.fork('pickup-place'));
    this.input = new Input(document.getElementById('app') as HTMLElement);
  }

  /** Install the fetched script. Called once at boot; a null script means onboarding is skipped. */
  setOnboardingScript(script: OnboardingScript | null): void {
    this.onboardingScript = script;
  }

  /** True while the run is inside the onboarding segment — the one place FS-01 R44 applies. */
  get inOnboarding(): boolean {
    return this.onboarding !== null && !this.onboarding.done;
  }

  /** Bearing of the node the chart last placed — what `camera-lean` biases toward (FS-01 §5.3). */
  get nodeLeanTheta(): number {
    return this.scheduler.lastNodeTheta;
  }

  /**
   * Has the player ever steered this run? FS-01 R10's control glyph is dismissed by the first
   * steering input — any input, not a beat-accurate one — and never returns.
   */
  hasSteered = false;

  /**
   * Did the run end while onboarding was still running? A death in the tutorial is not a score:
   * it must not set a best, and it must not name "Sector 0" on the results card. Latched at the
   * moment the run ends, because `inOnboarding` is false by the time the results screen reads it.
   */
  endedDuringOnboarding = false;

  /** FS-01 R15 — the skip control, offered from `ONBOARD_SKIP_OFFER_T`. */
  skipOnboarding(): void {
    if (!this.onboarding || this.onboarding.done) return;
    this.onboarding.end('skipped');
    this.onSegmentCleared();
  }

  attachInput(onFirstGesture: () => void): void {
    this.input.attach(onFirstGesture);
    this.bindTouchControls();
  }

  /** FS-12 R40 — the on-screen pause / use controls feed the same edges as the keyboard. */
  private bindTouchControls(): void {
    document.getElementById('btn-touch-pause')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.input.triggerPause();
    });
    document.getElementById('btn-touch-power')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.input.triggerPowerup();
    });
  }

  /** True when the platform's primary pointer is coarse — drives input naming (FS-07 R37). */
  get touchPrimary(): boolean {
    return this.input.getSteerModel() === 'direct';
  }

  setTraumaSink(fn: (amount: number) => void): void {
    this.onTrauma = fn;
  }

  get playing(): boolean {
    return this.machine.current === GameState.Playing;
  }

  get bossActive(): boolean {
    return this.bossFighting && !this.inTransition;
  }

  get transitioning(): boolean {
    return this.inTransition;
  }

  takePauseEdge(): boolean {
    return this.input.takePauseEdge();
  }

  /**
   * The sole constructor of run state (FS-09-R14, in spirit — see run.ts). `startSegmentIndex` is
   * the `movement-start` retry anchor (§4.5): the run's own stats always restart at zero (F20), only
   * the content position is preserved.
   */
  beginRun(config: RunConfig, startSegmentIndex = 0): void {
    this.config = config;
    this.plan = config.plan;
    this.customSector = config.custom
      ? customSectorDef(config.custom.bpm, config.custom.durationSeconds, ALL_KINDS)
      : null;

    this.rng = createRng(config.seed, 'run');
    this.grantRng = this.rng.fork('powerup-drop');
    this.pickups.reseed(this.rng.fork('pickup-place'));
    this.chartScheduler = new ChartScheduler(this.rng.fork('hazard-jitter'), this.audioClock);
    this.scheduler = this.chartScheduler;

    const hullMod = config.hull.makeModifier();
    this.modInput.accelScale = hullMod.accelScale;
    this.modInput.hitboxScale = hullMod.hitboxScale;
    this.modInput.beatAwardBonus = hullMod.beatAwardBonus;
    this.grantPool =
      config.hull.powerupPolicy === 'none'
        ? []
        : config.hull.powerupPolicy === 'no-shield'
          ? GRANTABLE.filter((p) => p !== 'Harmonic Shield')
          : GRANTABLE;

    this.director.reset();
    this.director.endlessBonus = 0;
    this.resonance.reset();
    this.resonance.scoreEnabled = config.mode.rules.scoreEnabled;
    this.resonance.hullScoreMult = config.hull.scoreMultiplier;
    this.powerups.reset();
    if (config.hull.startingPowerup) this.powerups.stored = config.hull.startingPowerup;
    resetShip(this.ship, config.hull.hitPoints);
    this.hazards.releaseAll();

    this.hazardsCleared = 0;
    this.highestSectorCleared = 0;
    this.hitsTakenCount = 0;
    this.staggerCount = 0;
    this.loopCount = 0;
    this.grantTimer = POWERUP_GRANT_INTERVAL;
    this.pickups.reset();
    this.lastBeatSteerBeat = -1;
    this.prevAxSign = 0;
    this.deathCause = '';
    this.won = false;
    this.hasSteered = false;
    this.endedDuringOnboarding = false;
    this.bossTriggered = false;
    this.bossFighting = false;

    this.loadSegment(Math.min(startSegmentIndex, this.plan.length - 1));

    this.bus.emit('run:start', { seed: config.seed, startTick: 0 });
  }

  /** Swap the active sector/segment. Ship, score, Resonance and multiplier carry across (FS-09 §4.3). */
  private loadSegment(index: number): void {
    this.segmentIndex = index;
    const seg = this.plan[index]!;
    this.sector = seg.sectorIndex === -1 ? this.customSector! : sectorByIndex(seg.sectorIndex);

    // A segment with a script gets the scripted scheduler; everything else gets the live chart.
    // This is the whole of onboarding's integration into the run orchestrator (FS-09 R02).
    if (seg.scriptId === 'onboarding' && this.onboardingScript) {
      this.onboarding = new OnboardingScheduler(
        this.onboardingScript,
        this.rng.fork('onboarding'),
        this.audioClock,
        this.bus,
      );
      this.scheduler = this.onboarding;
    } else {
      this.onboarding = null;
      // The run's own chart scheduler, built once in `beginRun` and reset per segment — its RNG
      // stream must stay continuous across segments or every existing run's seed changes meaning.
      this.scheduler = this.chartScheduler;
    }

    this.scheduler.setAllowList(this.sector.allow);
    this.scheduler.reset();
    this.audioClock.bpm = this.sector.bpm;
    this.audioClock.reset();
    this.director.reset();
    this.hazards.releaseAll();
    this.bossTriggered = false;
    this.bossFighting = false;
    this.boss.stop();
    this.bossRng = this.rng.fork(`boss-pattern:${index}:${this.loopCount}`);

    this.inTransition = seg.transitionBars > 0;
    this.transitionSecondsLeft = this.inTransition ? (seg.transitionBars * 4 * 60) / this.sector.bpm : 0;

    this.bus.emit('segment:changed', { sectorIndex: this.sector.index, sectorId: this.sector.id, bpm: this.sector.bpm });
  }

  swapBuffers(): void {
    // Previous transforms are captured inside step (flight sets prevX/Y, hazards set prevZ).
  }

  step(dt: number, _tick: number): void {
    if (this.machine.current !== GameState.Playing) return;

    // 1. Input (sampled once per frame; identical across sub-steps within a frame). The ship is
    //    passed so the 'direct' touch model can close its position loop (FS-05 R45); only this
    //    method's output reaches the sim, so determinism is untouched (R49).
    this.input.sample(_tick, this.frame, this.ship);
    if (!this.hasSteered && Math.abs(this.frame.ax) > AXIS_DEADZONE) this.hasSteered = true;

    // 2. Advance the master timebase — frozen during a transition (its own plain countdown below),
    //    so the beat grid a segment's scheduler reads always starts at 0 for that segment.
    if (this.inTransition) {
      this.transitionSecondsLeft -= dt;
      if (this.transitionSecondsLeft <= 0) this.inTransition = false;
    } else {
      this.audioClock.advance(dt);
    }

    // 3. Beat-accurate steering (Design Bible §8).
    this.detectBeatSteer();

    // 4. Resolve the modifier stack.
    this.modInput.intangible = this.powerups.intangible;
    this.modInput.worldSpeedScale = this.powerups.worldSpeedScale;
    resolveModifiers(this.modInput, this.mods);

    // 5. Director + speed.
    this.director.setOverdrive(this.resonance.overdrive);
    this.director.step(dt);
    const scroll = this.director.scrollSpeed * this.mods.worldSpeedScale;
    const dz = scroll * dt;

    // 6. Schedule spawns / the boss — the boss occupies the Finale movement (FS-08 R06), or the
    //    whole segment for Gauntlet (`bossOnly`, FS-09 R23).
    if (!this.inTransition) {
      const seg = this.plan[this.segmentIndex]!;
      if (seg.bossOnly) {
        if (!this.bossTriggered) {
          this.bossTriggered = true;
          this.bossFighting = true;
          this.boss.start(BOSSES[this.sector.bossId]!, this.audioClock);
        }
      } else if (!this.bossTriggered && !this.sector.noBoss && this.audioClock.time >= this.sector.duration) {
        this.bossTriggered = true;
        this.bossFighting = true;
        this.boss.start(BOSSES[this.sector.bossId]!, this.audioClock);
      }

      if (this.bossFighting) {
        // Phase tempo overrides (Gravity Well accelerates 160→200; The Fundamental modulates).
        const bpm = this.boss.phaseBpm;
        if (bpm > 0 && this.audioClock.bpm !== bpm) this.audioClock.bpm = bpm;
        this.boss.update(this.audioClock, this.emitBossHazard);
        if (this.boss.done) {
          this.bossFighting = false;
          this.onSegmentCleared();
          return;
        }
      } else if (!seg.bossOnly) {
        // Sector 7 accelerates across the sector (Design Bible §9.3).
        if (this.sector.bpmEnd) {
          const t = Math.min(1, this.audioClock.time / this.sector.duration);
          this.audioClock.bpm = this.sector.bpm + (this.sector.bpmEnd - this.sector.bpm) * t;
        }
        // §5.2 bar 29.1's Overdrive guarantee reads the live meter. The scheduler never reads ship
        // *position* — that would make the teach adaptive, which FS-08 R37 forbids — only whether
        // the player has found Resonance on their own.
        if (this.onboarding) this.onboarding.setResonance(this.resonance.value);
        this.scheduler.update(this.director, (req) => this.spawn(req));
        if (this.sector.noBoss && this.audioClock.time >= this.sector.duration) {
          this.onSegmentCleared();
          return;
        }
      }
    }

    // 7. Flight.
    stepFlight(this.ship, this.frame, dt, this.mods, this.world);

    // 8. Advance + test + cull hazards (iterate downward so swap-remove is safe).
    for (let i = this.hazards.live - 1; i >= 0; i--) {
      const h = this.hazards.items[i]!;
      h.prevZ = h.z;
      h.z += dz;
      h.tLocal += dt;
      if (h.dissolve > 0 && h.dissolve < 1) h.dissolve = Math.min(1, h.dissolve + dt / 0.4);

      testCollision(this.ship, h, dz, this.mods, this.collision);
      this.resolveHazard(h);

      if (h.z > CULL_Z) {
        if (!h.resolved) {
          this.resonance.onHazardCleared();
          this.hazardsCleared++;
          this.bus.emit('player:hazardCleared', { id: h.id });
        }
        this.bus.emit('hazard:culled', { id: h.id, cleared: !h.resolved });
        this.hazards.release(h);
      }
    }

    // 9. Meters — the idle drain during a transition is real (Gauntlet's tension, FS-09 §4.3).
    this.resonance.step(dt, dz, this.director.intensity);

    // 10. Power-ups.
    if (this.frame.usePowerup) this.powerups.use();
    this.powerups.step(dt);
    // 11. Pickups. Power-ups are **collected**, not handed out on a timer (FS-07 §2.5): the timer
    //     meant the eight power-ups had no presence in the playfield and nothing to learn.
    this.grantTimer -= dt;
    if (this.grantTimer <= 0 && this.grantPool.length > 0) {
      const firstEver = !this.seenAPickup;
      const spawned = this.pickups.spawn(
        this.grantRng.pick(this.grantPool),
        this.scheduler.lastNodeTheta,
        this.scheduler.lastNodeHalfTheta,
        firstEver,
      );
      if (spawned) {
        this.seenAPickup = true;
        this.grantTimer = POWERUP_GRANT_INTERVAL;
      }
    }
    this.pickups.step(dz, this.ship.x, this.ship.y, (p) => {
      // F9 — collecting with the slot full swaps; the displaced one is gone, not queued.
      this.powerups.grant(p.kind);
    });
  }

  private resolveHazard(h: Hazard): void {
    const c = this.collision;
    if (c.nodeCentre && !h.nodeAwarded && h.lethal) {
      h.nodeAwarded = true;
      this.resonance.onNodeCentre();
      this.bus.emit('player:nodeCentre', { hazardId: h.id });
    }
    if (c.outcome === CollisionOutcome.Graze) {
      if (!h.grazed && h.lethal) {
        h.grazed = true;
        this.resonance.onGraze();
        this.onTrauma(0.12);
        this.bus.emit('player:graze', { hazardId: h.id, distance: c.distance, nx: c.nx, ny: c.ny });
      }
    } else if (c.outcome === CollisionOutcome.Hit) {
      if (!h.resolved && this.ship.iframes <= 0) {
        h.resolved = true;
        this.handleHit(h);
      }
    }
  }

  private handleHit(h: Hazard): void {
    this.hitsTakenCount++;
    // Hit resolution chain (FS-05 §4.5), slice: shield → hull HP → death.
    if (this.powerups.absorbHit()) {
      this.ship.iframes = IFRAME_DURATION;
      this.resonance.onHit();
      this.onTrauma(0.45);
      this.bus.emit('player:hit', { hazardId: h.id, fatal: false });
      return;
    }
    if (this.powerups.absorbDecoy()) {
      this.ship.iframes = IFRAME_DURATION;
      this.resonance.onHit();
      this.onTrauma(0.45);
      this.bus.emit('player:hit', { hazardId: h.id, fatal: false });
      return;
    }
    if (this.ship.hp > 1) {
      this.ship.hp -= 1;
      this.ship.iframes = IFRAME_DURATION;
      this.resonance.onHit();
      this.onTrauma(0.7);
      this.bus.emit('player:hit', { hazardId: h.id, fatal: false });
      return;
    }
    if (this.config && !this.config.mode.rules.deathEnabled) {
      // Zen (FS-09 R24): a lethal hit becomes a non-scoring stagger with i-frames. Resonance and
      // the multiplier reset exactly as a normal hit would (R24's "resonance -> 0, multiplier -> 1");
      // position and velocity are untouched (FS-05 R34) and the run never ends.
      this.ship.iframes = IFRAME_DURATION;
      this.resonance.onHit();
      this.staggerCount++;
      this.onTrauma(0.7);
      this.bus.emit('player:hit', { hazardId: h.id, fatal: false });
      this.bus.emit('player:stagger', { hazardId: h.id });
      return;
    }
    // Death.
    // A death during onboarding still ends onboarding (FS-01 §4.3's `reason: 'died'`). A player
    // who met the game at 20 s has met it; replaying the tutorial after every death would be the
    // worst version of R14.
    this.endedDuringOnboarding = this.inOnboarding;
    this.onboarding?.end('died');
    // Name where the fault happened — the boss phase during a fight, the movement otherwise.
    this.deathCause = this.bossFighting
      ? `${h.def.kind} · ${this.boss.name} — ${this.boss.phaseName}`
      : `${h.def.kind} · ${this.director.movementName}`;
    this.onTrauma(0.9);
    this.lastEndedSegmentIndex = this.segmentIndex;
    this.bus.emit('player:hit', { hazardId: h.id, fatal: true });
    this.bus.emit('run:end', { reason: 'death', score: this.resonance.score, endTick: 0 });
    this.machine.request(GameState.Results, 'death');
  }

  private wavebreaker(): void {
    for (let i = 0; i < this.hazards.live; i++) {
      const h = this.hazards.items[i]!;
      if (h.z > -70 && h.lethal) {
        h.lethal = false;
        h.dissolve = 0.001;
      }
    }
  }

  private emitBossHazard = (
    kind: HazardKind,
    nodeTheta: number,
    intensity: number,
    opts?: { nodeScale?: number },
  ): void => {
    const def = HAZARD_KINDS[kind];
    configureParams(def, intensity, nodeTheta, this.bossRng, this.audioClock, this.bossParams);
    // Boss phases usually relax the gap; the guarantee is a floor, never a ceiling (FS-08 R24).
    if (opts?.nodeScale) this.bossParams.nodeHalfTheta *= opts.nodeScale;
    this.spawn({ def, params: this.bossParams, z: SPAWN_Z, targetBeat: 0, intensity });
  };

  /** A segment's content ended — either its boss went down or (Custom Track) the track ended. */
  private onSegmentCleared(): void {
    const seg = this.plan[this.segmentIndex]!;
    if (seg.sectorIndex > this.highestSectorCleared) this.highestSectorCleared = seg.sectorIndex;
    // Onboarding is not a sector cleared: no bonus, no unlock credit, no entry in the ladder.
    // Paying 5 000 points for finishing the tutorial would corrupt every leaderboard it precedes.
    if (!seg.scriptId) this.resonance.score += SECTOR_CLEAR_BONUS;

    const isLastSegment = this.segmentIndex + 1 >= this.plan.length;
    if (!isLastSegment) {
      this.loadSegment(this.segmentIndex + 1);
      return;
    }

    const modeId = this.config?.modeId;
    if (modeId === 'infinite' || modeId === 'zen') {
      // Endless (FS-09 §4.3): loop the plan. Infinite bumps the ramp once per loop of content —
      // never per elapsed time (R21) — Zen loops at the charts' own authored pace forever.
      if (this.config?.mode.rules.endlessRamp) {
        this.loopCount++;
        this.director.endlessBonus = Math.min(ENDLESS_INTENSITY_CAP, this.loopCount * ENDLESS_INTENSITY_STEP);
      }
      this.loadSegment(0);
      return;
    }

    this.won = true;
    this.deathCause = this.sector.noBoss ? 'Track complete' : `${this.boss.name} down`;
    this.lastEndedSegmentIndex = this.segmentIndex;
    this.bus.emit('run:end', { reason: 'complete', score: this.resonance.score, endTick: 0 });
    this.machine.request(GameState.Results, 'run-complete');
  }

  /** Abandon the current run (FS-09 R19) — still resolves to Results so a run record is written. */
  abandonRun(): void {
    if (this.machine.current !== GameState.Playing && this.machine.current !== GameState.Paused) return;
    this.endedDuringOnboarding = this.inOnboarding;
    this.onboarding?.end('quit');
    this.won = false;
    this.deathCause = 'Abandoned';
    this.lastEndedSegmentIndex = this.segmentIndex;
    this.bus.emit('run:end', { reason: 'quit', score: this.resonance.score, endTick: 0 });
    this.machine.request(GameState.Results, 'abandon');
  }

  /** Live caps for the two kinds that dominate the frame in quantity (FS-08 §4.4). */
  private atLiveCap(kind: HazardKind): boolean {
    const cap = kind === 'SubBassSlab' ? MAX_LIVE_SLAB : kind === 'FeedbackLoop' ? MAX_LIVE_FEEDBACK : 0;
    if (cap === 0) return false;
    let n = 0;
    for (let i = 0; i < this.hazards.live; i++) {
      if (this.hazards.items[i]!.def.kind === kind && ++n >= cap) return true;
    }
    return false;
  }

  private spawn(req: SpawnRequest): void {
    if (this.atLiveCap(req.def.kind)) return;
    const h = this.hazards.acquire();
    if (!h) return; // pool full — drop (documented policy)
    // Null Field flattens amplitude only (never narrows the node — FS-06 R30).
    const scale = this.powerups.nullFieldScale();
    if (scale !== 1) req.params.amp *= scale;
    h.spawn(req.def, req.params, req.z, req.targetBeat, req.intensity, req.lethal !== false);
    this.bus.emit('hazard:spawned', { id: h.id, kind: req.def.kind, targetBeat: req.targetBeat });

    // A scripted pickup rides the hazard's node (FS-01 §5.2's first Harmonic Shield, placed at the
    // node centre so it cannot be avoided without deliberately taking the hard line).
    if (req.pickup) {
      const firstEver = !this.seenAPickup;
      if (this.pickups.spawn(req.pickup, req.params.nodeTheta, req.params.nodeHalfTheta, firstEver)) {
        this.seenAPickup = true;
      }
    }
  }

  private detectBeatSteer(): void {
    const ax = this.frame.ax;
    const sign = Math.abs(ax) < AXIS_DEADZONE ? 0 : Math.sign(ax);
    if (sign !== 0 && sign !== this.prevAxSign) {
      const beat = this.audioClock.beat;
      const beatInt = Math.floor(beat);
      const frac = beat - beatInt;
      const toNearest = Math.min(frac, 1 - frac) / this.audioClock.bps;
      const nearestBeat = frac < 0.5 ? beatInt : beatInt + 1;
      if (toNearest < BEAT_WINDOW_S && nearestBeat !== this.lastBeatSteerBeat) {
        this.lastBeatSteerBeat = nearestBeat;
        this.resonance.onBeatSteer(this.mods.beatAwardBonus);
        this.bus.emit('player:beatSteer', { simTick: 0 });
      }
    }
    if (sign !== 0) this.prevAxSign = sign;
  }
}
